/**
 * Post-add-to-cart cross-sell modal.
 *
 * <product-form> uses this in place of the cart drawer whenever the element is present —
 * which snippets/add-on-modal.liquid only renders when the product's curated
 * custom.complementary_products list holds at least one available product.
 *
 * Cards are already in the DOM (rendered server-side from the metafield). All this does at
 * runtime is drop anything the customer already has in their cart, and hand back to the
 * normal cart drawer if that leaves nothing worth interrupting them for. The drawer is
 * refreshed from the same /cart/add.js response either way.
 */
if (!customElements.get('add-on-modal')) {
  class AddOnModal extends ModalDialog {
    constructor() {
      super();
      this.cartDrawer = document.querySelector('cart-drawer');
      this.addedEl = this.querySelector('[data-add-on-added]');
      this.recsEl = this.querySelector('[data-add-on-recs]');
      this.recsListEl = this.querySelector('[data-add-on-recs-list]');
      this.errorEl = this.querySelector('[data-add-on-error]');
      this.onRecClick = this.onRecClick.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', this.onRecClick);
    }

    /**
     * Entry point used by <product-form>, mirroring the cart drawer's own contract.
     */
    async renderContents(parsedState) {
      const hasRecommendations = await this.filterRecommendations();

      if (!hasRecommendations) {
        // Nothing left to offer, so don't interrupt with an empty modal —
        // let the drawer open exactly as it would without this feature.
        this.openCartDrawer(parsedState);
        return;
      }

      this.renderAddedItem(parsedState);
      this.syncCartDrawer(parsedState);
      this.show();
    }

    /** Opens the cart drawer normally, the no-cross-sell fallback. */
    openCartDrawer(parsedState) {
      if (!this.cartDrawer || typeof this.cartDrawer.renderContents !== 'function') {
        window.location = window.routes.cart_url;
        return;
      }
      this.cartDrawer.renderContents(parsedState);
    }

    /** Keeps the drawer's markup current without opening it. */
    syncCartDrawer(parsedState) {
      if (!this.cartDrawer || typeof this.cartDrawer.renderContents !== 'function') return;
      try {
        this.cartDrawer.renderContents(parsedState, true);
      } catch (error) {
        console.warn('Unable to refresh cart drawer behind the add-on modal:', error);
      }
    }

    /**
     * The /cart/add.js response describes the line just added. Only its thumbnail is shown,
     * in the header: the customer picked this product moments ago, so the modal confirms
     * what was added rather than re-describing it.
     */
    renderAddedItem(parsedState) {
      const item = Array.isArray(parsedState?.items) ? parsedState.items[0] : parsedState;
      const image = item?.featured_image?.url || item?.image;

      if (!image) {
        this.addedEl.innerHTML = '';
        return;
      }

      const alt = this.escape(item.product_title || item.title || '');
      this.addedEl.innerHTML = `<img class="add-on-modal__added-image" src="${this.resizedImage(
        image
      )}" alt="${alt}" width="64" height="64">`;
    }

    /**
     * Removes cards for products already in the cart.
     * @returns {Promise<boolean>} whether any recommendation is left to show.
     */
    async filterRecommendations() {
      const cards = Array.from(this.recsListEl.querySelectorAll('.add-on-modal__rec-card'));
      if (!cards.length) return false;

      const inCart = await this.cartProductIds();
      const remaining = cards.filter((card) => {
        if (!inCart.has(card.dataset.productId)) return true;
        card.remove();
        return false;
      });

      if (!remaining.length) return false;

      // The highlight is server-rendered onto the first card, which may have just been
      // removed — move it to whatever now leads.
      if (!remaining[0].classList.contains('add-on-modal__rec-card--featured')) {
        remaining[0].classList.add('add-on-modal__rec-card--featured');
      }

      this.recsEl.hidden = false;
      this.publishImpressions(remaining);
      return true;
    }

    async cartProductIds() {
      try {
        const cart = await fetch(`${this.dataset.cartUrl}.js`).then((response) => response.json());
        return new Set((cart.items || []).map((item) => String(item.product_id)));
      } catch {
        return new Set();
      }
    }

    onRecClick(event) {
      const button = event.target.closest('[data-add-on-add]');
      if (!button) return;
      event.preventDefault();
      this.addRecommendation(button);
    }

    async addRecommendation(button) {
      if (button.disabled) return;
      button.disabled = true;
      this.errorEl.hidden = true;

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items: [
            {
              id: button.dataset.variantId,
              quantity: 1,
              properties: { __cross_sell: 'pdp_modal' },
            },
          ],
          sections: this.cartDrawer ? this.cartDrawer.getSectionsToRender().map((s) => s.id) : [],
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());
        if (response.status) throw new Error(response.description || 'Add to cart failed');

        this.markAdded(button);
        this.syncCartDrawer(response);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          // 'add-on-modal' keeps luxo-loaders from opening the drawer over this modal.
          source: 'add-on-modal',
          productVariantId: button.dataset.variantId,
          cartData: response,
        });

        if (typeof window.publishCrossSellEvent === 'function') {
          window.publishCrossSellEvent('cross_sell_added', {
            placement: 'pdp_modal',
            product_id: button.dataset.productId,
            variant_id: button.dataset.variantId,
            value: (Number(button.dataset.price) || 0) / 100,
            parent_product_id: this.dataset.productId,
          });
        }
      } catch (error) {
        console.warn('Unable to add recommended product:', error);
        // Message is rendered server-side so it stays translated and works whether or not
        // the inline add-on placement (which also defines addonsStrings) is enabled.
        this.errorEl.hidden = false;
        button.disabled = false;
      }
    }

    markAdded(button) {
      button.classList.add('is-added');
      button.querySelector('.add-on-modal__rec-add-idle')?.setAttribute('hidden', '');
      button.querySelector('.add-on-modal__rec-add-added')?.removeAttribute('hidden');
    }

    publishImpressions(cards) {
      if (this.impressionsPublished || typeof window.publishCrossSellEvent !== 'function') return;
      this.impressionsPublished = true;

      cards.forEach((card) => {
        // Multi-variant cards link to their product page instead of carrying an add
        // button, so read identity off the card and the variant only when present.
        const button = card.querySelector('[data-add-on-add]');
        window.publishCrossSellEvent('cross_sell_impression', {
          placement: 'pdp_modal',
          product_id: card.dataset.productId,
          variant_id: button?.dataset.variantId,
          value: (Number(button?.dataset.price) || 0) / 100,
          parent_product_id: this.dataset.productId,
        });
      });
    }

    resizedImage(url) {
      return url.includes('?') ? url : `${url}${url.includes('_160x') ? '' : '?width=160'}`;
    }

    escape(value) {
      const node = document.createElement('div');
      node.textContent = value ?? '';
      return node.innerHTML;
    }

    /** <product-form> asks the cart target for the sections to request. */
    getSectionsToRender() {
      return this.cartDrawer ? this.cartDrawer.getSectionsToRender() : [];
    }

    setActiveElement(element) {
      this.openedBy = element;
    }
  }

  customElements.define('add-on-modal', AddOnModal);
}
